import {
	addUnit,
	dateToQuery,
	dateToUTCString,
	endOf,
	formatDate,
	parseDateFromQuery,
	startOf,
	subtractUnit,
} from "@sembark-travel/datetime-utils"
import {
	AsyncSelect,
	type AsyncSelectProps,
	Badge,
	Box,
	Button,
	Container,
	Heading,
	Icons,
	Inline,
	RelativeTime,
	SelectInput,
	Stack,
	Table,
	Text,
	Tooltip,
} from "@sembark-travel/ui-base"
import {
	areAdvancedFiltersAppliedDefault,
	ListView,
	Search,
	type TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { ButtonLink, Link, useLocationQuery } from "@sembark-travel/ui-router"
import { useXHR } from "@sembark-travel/xhr"
import { useEffect, useMemo } from "react"
import useSWR from "swr"
import { getParsingBadge, type TParsingSnapshot } from "../MailMessages/parsing"
import { generatePath } from "../router-utils"
import { PERMISSIONS, useAuthUser, useCheckPermissions } from "../Auth"
import { SelectField, SelectInputField } from "@sembark-travel/ui-form"
import {
	WalletBalanceCard,
	type TWalletStats,
} from "../SubscriptionWalletCredits"

type TIntervalType = "day" | "week" | "month"

type TStatusFilter = "skipped" | "completed" | undefined

type TTenantOption = { id: number; name: string }

export type TUsageViewFilters = TSearchParams & {
	intervalType: TIntervalType
	after: Date
	before: Date
	status?: TStatusFilter
	tenant?: TTenantOption | null
}

type TFiltersInQuery = TSearchParams & {
	intervalType: TIntervalType
	after: string
	before: string
	status?: TStatusFilter
	tenant?: string
}

type TParsingItem = TParsingSnapshot & {
	mail_message?: {
		id: number | null
		subject?: string | null
		from_name?: string | null
		received_at?: string | null
		provider_message_id?: string | null
		is_orphaned?: boolean
		mailbox?: {
			id: number
			name?: string | null
			email?: string | null
			is_deleted?: boolean
		} | null
	} | null
}

type TUsageStats = {
	data: {
		range: {
			after: string | null
			before: string | null
			interval_type: TIntervalType
		}
		total: number
		skipped: {
			total: number
			by_reason: Record<string, number>
		}
		ai_processed: {
			total: number
			by_classification: Record<string, number>
		}
		avg_credits_per_parse?: number | null
		wallet?: (TWalletStats & { approx_remaining_parses?: number | null }) | null
		supplier_cost?: Array<{ currency: string; total: number }> | null
		over_threshold_count?: {
			classified: number
			unclassified: number
			invalid: number
			classified_overrun: string
			unclassified_overrun: string
			invalid_overrun: string
			thresholds: {
				classified: number | null
				unclassified: number | null
				invalid: number | null
			}
		} | null
	}
}

const CLASSIFICATION_LABELS: Record<string, string> = {
	quotation_request: "Lead",
	support: "Support",
	not_relevant: "Not Relevant",
	no_enquiry_found: "No Enquiry Found",
	spam: "Spam",
}

const CLASSIFICATION_LABELS__FOR_TICKETS: Record<string, string> = {
	[CLASSIFICATION_LABELS["quotation_request"]]: "Queries",
	[CLASSIFICATION_LABELS["support"]]: "Queries",
}

function SelectMailParsingTenants(props: Omit<AsyncSelectProps, "fetch">) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			cacheKey="mail-parsing-tenants"
			fetch={(q) =>
				xhr
					.get("/mail-message-parsings/tenants", {
						params: { q },
					})
					.then((resp) => resp.data.data)
			}
			{...props}
		/>
	)
}

const SKIP_REASON_LABELS: Record<string, string> = {
	already_linked: "Already Linked",
	thread_has_history: "Thread Reply",
	parser_disabled: "Parser Disabled",
	credit_exhausted: "Credit Exhausted",
	provider_key_missing: "AI Not Enabled",
	empty_content: "Awaiting Content",
	stale_backlog: "Backlog",
}

export function MailParsingUsageView() {
	const { user } = useAuthUser()
	const { hasPermission } = useCheckPermissions()
	const adminMode = !user?.tenant_id
	const canViewWallet = hasPermission(PERMISSIONS.MANAGE_TENANTS)
	const [query, setQuery] = useLocationQuery<
		TUsageViewFilters,
		TFiltersInQuery
	>({
		toQuery: filtersToQuery,
		fromQuery: queryToFilters,
	})
	const [params, setParams] = useSearch<TUsageViewFilters>(query)

	const requestParams = useMemo(
		() => ({
			page: params.page || 1,
			q: params.q || undefined,
			after: dateToUTCString(params.after),
			before: dateToUTCString(params.before),
			status: params.status || undefined,
			interval_type: params.intervalType,
			tenant_id: adminMode && params.tenant ? params.tenant.id : undefined,
		}),
		[params, adminMode]
	)

	const statsParams = useMemo(() => {
		const { page: _page, ...rest } = requestParams
		return rest
	}, [requestParams])

	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])

	const xhr = useXHR()
	const { data: stats } = useSWR<TUsageStats>(
		["mail-parsing-usage-stats", statsParams, adminMode ? "admin" : "tenant"],
		() =>
			xhr
				.get("/mail-message-parsings/usage-stats", {
					params: statsParams,
				})
				.then((resp) => resp.data)
	)

	const aiTotal = stats?.data.ai_processed.total ?? 0
	const aiByClassification = stats?.data.ai_processed.by_classification ?? {}
	const skippedTotal = stats?.data.skipped.total ?? 0
	const skippedByReason = stats?.data.skipped.by_reason ?? {}

	return (
		<Search
			title={"Mail Parsing Usage"}
			initialParams={params}
			onSearch={setParams}
			placeholder="Search subject"
			Filters={Filters}
			resetParams={(p) => ({
				q: "",
				page: 1,
				after: p.after,
				before: p.before,
				intervalType: p.intervalType,
				status: undefined,
			})}
			areAdvancedFiltersApplied={(params) => {
				const { after, before, intervalType, ...others } = params
				return areAdvancedFiltersAppliedDefault(others)
			}}
			actions={({ setSearchParams, searchParams }) => (
				<Inline alignItems="center" gap="4">
					<Inline alignItems="center" gap="2">
						<Button
							onClick={() => {
								const after = startOf(
									subtractUnit(
										searchParams.after,
										1,
										searchParams.intervalType
									),
									searchParams.intervalType
								)
								setSearchParams({
									after,
									before: endOf(after, searchParams.intervalType),
									page: 1,
								})
							}}
						>
							<Icons.ChevronDown rotate="90" />
						</Button>
						<Box>
							<SelectInput
								value={searchParams.intervalType}
								onChange={(e) => {
									const type = (e.currentTarget.value ||
										"month") as TIntervalType
									setSearchParams({
										intervalType: type,
										after: startOf(new Date(), type),
										before: endOf(new Date(), type),
										page: 1,
									})
								}}
							>
								<option value="month">Month</option>
								<option value="week">Week</option>
								<option value="day">Day</option>
							</SelectInput>
						</Box>
						<Button
							onClick={() => {
								const after = startOf(
									addUnit(searchParams.after, 1, searchParams.intervalType),
									searchParams.intervalType
								)
								setSearchParams({
									after,
									before: endOf(after, searchParams.intervalType),
									page: 1,
								})
							}}
						>
							<Icons.ChevronDown rotate="270" />
						</Button>
					</Inline>
					{adminMode ? null : (
						<ButtonLink to={generatePath("/mail-messages")}>
							<Icons.Mail /> Mails
						</ButtonLink>
					)}
				</Inline>
			)}
		>
			<Container paddingY="4">
				<Stack gap="4">
					<Heading as="h5" fontSize="lg">
						{formatDate(params.after, "ddd D MMM")} -{" "}
						{formatDate(params.before, "ddd D MMM")}
					</Heading>
					<Inline paddingY="2" bgColor="default" flexWrap="wrap">
						<Stack paddingX="6" paddingY="2" gap="2">
							<Text color="muted">Total</Text>
							<Text fontSize="xl" fontWeight="semibold">
								{!stats ? "..." : (stats?.data.total ?? 0)}
							</Text>
						</Stack>
						<Stack borderLeftWidth="4" paddingX="6" paddingY="2" gap="2">
							<Text fontWeight="semibold">
								Smart Skip{" "}
								<Tooltip
									interactive
									content={
										<Stack gap="3">
											<Text>
												To minimize the AI Credit Usage, system smartly skips
												emails.
											</Text>
											{skippedTotal ? (
												<>
													<Text fontSize="xs">
														Following are the reasons for skips
													</Text>
													{Object.entries(SKIP_REASON_LABELS)
														.filter(([key]) => skippedByReason[key])
														.map(([key, label]) => (
															<Text key={key} whiteSpace="nowrap">
																{label}: {skippedByReason[key] ?? 0}
															</Text>
														))}
												</>
											) : null}
										</Stack>
									}
								>
									<Box as="span">
										<Icons.Info />
									</Box>
								</Tooltip>
							</Text>
							<Text fontSize="xl" fontWeight="semibold">
								{skippedTotal}
							</Text>
						</Stack>
						<Stack borderLeftWidth="4" paddingX="6" paddingY="2" gap="2">
							<Text color="success" fontWeight="semibold">
								<Icons.SparklesSolid color="warning" /> AI Processed
							</Text>
							<Inline flexWrap="wrap" gap="2" alignItems="baseline">
								<Text fontSize="xl" fontWeight="semibold">
									{aiTotal}
								</Text>
								{aiTotal ? (
									<Inline gap="3" flexWrap="wrap">
										{Object.entries(
											Object.entries(CLASSIFICATION_LABELS)
												.filter(([key]) => aiByClassification[key])
												.reduce<Record<string, number>>(
													(grouped, [key, label]) => {
														const value = aiByClassification[key]
														const groupLabel =
															CLASSIFICATION_LABELS__FOR_TICKETS[label] ||
															"Others"
														if (!grouped[groupLabel]) {
															grouped[groupLabel] = value
														} else {
															grouped[groupLabel] += value
														}
														return grouped
													},
													{}
												)
										).map(([label, value]) => (
											<Text
												key={label}
												fontSize="sm"
												color="muted"
												whiteSpace="nowrap"
											>
												{label}: {value}
											</Text>
										))}
									</Inline>
								) : null}
							</Inline>
						</Stack>
						{stats?.data?.avg_credits_per_parse != null ? (
							<Stack borderLeftWidth="4" paddingX="6" paddingY="2" gap="2">
								<Text fontWeight="semibold">
									<Icons.BankNotes /> Avg. AI
								</Text>
								<Inline flexWrap="wrap" gap="2" alignItems="baseline">
									<Text
										fontSize="xl"
										fontWeight="semibold"
										title="Average wallet credits debited per parse in this range"
									>
										{Number(stats.data.avg_credits_per_parse).toFixed(2)}
									</Text>
									<Text fontSize="sm" color="muted" whiteSpace="nowrap">
										Credits
									</Text>
								</Inline>
							</Stack>
						) : null}
						{stats?.data?.wallet ? (
							<Stack borderLeftWidth="4" paddingX="6" paddingY="2" gap="2">
								<WalletBalanceCard
									wallet={stats?.data.wallet}
									adminTenantId={
										adminMode && canViewWallet && params.tenant
											? params.tenant.id
											: undefined
									}
								/>
								{stats.data.wallet.approx_remaining_parses != null ? (
									<Text
										fontSize="sm"
										color="muted"
										title="Approx parses the remaining balance can cover at the average cost"
									>
										≈{" "}
										{stats.data.wallet.approx_remaining_parses.toLocaleString()}{" "}
										parses left
									</Text>
								) : null}
							</Stack>
						) : null}
						{stats?.data?.supplier_cost ? (
							<Stack borderLeftWidth="4" paddingX="6" paddingY="2" gap="2">
								{adminMode && canViewWallet && params.tenant ? (
									<Link
										to={generatePath(
											"/admin/tenants/:tenantId/subscription-wallet",
											{ tenantId: String(params.tenant.id) }
										)}
									>
										<Text fontWeight="semibold">
											<Icons.BankNotes /> Supplier cost
										</Text>
									</Link>
								) : (
									<Text fontWeight="semibold">
										<Icons.BankNotes /> Supplier cost
									</Text>
								)}
								<Stack gap="1">
									{stats.data.supplier_cost.length === 0 ? (
										<Text color="muted">No vendor cost yet.</Text>
									) : (
										stats.data.supplier_cost.map((row) => (
											<Inline key={row.currency} gap="1" alignItems="baseline">
												<Text as="sup" fontWeight="semibold">
													{row.currency}
												</Text>
												<Text fontSize="lg" fontWeight="semibold">
													{Number(row.total).toFixed(6)}
												</Text>
											</Inline>
										))
									)}
								</Stack>
							</Stack>
						) : null}
						{stats?.data?.over_threshold_count ? (
							<Stack borderLeftWidth="4" paddingX="6" paddingY="2" gap="2">
								<Text fontWeight="semibold">
									<Icons.Attention color="warning" /> Over cost threshold
								</Text>
								<Inline gap="2">
									{(
										[
											["classified", "Classified"],
											["unclassified", "Unclassified"],
											["invalid", "Invalid"],
										] as const
									).map(([key, label]) => {
										const count = stats.data.over_threshold_count?.[key] ?? 0
										if (!count) return null
										const overrunKey =
											`${key}_overrun` as `${typeof key}_overrun`
										const overrun =
											Number(stats.data.over_threshold_count?.[overrunKey]) || 0
										return (
											<Stack key={key} gap="px">
												<Inline gap="1" alignItems="baseline">
													<Text color="warning" fontWeight="semibold">
														{count}
													</Text>
													<Text color="muted" fontSize="sm">
														{label}
													</Text>
												</Inline>
												{overrun ? (
													<Text
														fontSize="sm"
														color="warning"
														title={`Threshold: $${((stats.data.over_threshold_count?.thresholds?.[key] ?? 0) * count).toFixed(6)} (${count} × $${(stats.data.over_threshold_count?.thresholds?.[key] ?? 0).toFixed(6)})`}
													>
														+${overrun.toFixed(6)}
													</Text>
												) : null}
											</Stack>
										)
									})}
									{!stats.data.over_threshold_count.classified &&
									!stats.data.over_threshold_count.unclassified &&
									!stats.data.over_threshold_count.invalid ? (
										<Text color="muted">None exceeded threshold.</Text>
									) : null}
								</Inline>
							</Stack>
						) : null}
					</Inline>
				</Stack>
			</Container>
			<ListView<TParsingItem, TUsageViewFilters>
				params={params}
				pageKey={adminMode ? "admin-mail-parsing-usage" : "mail-parsing-usage"}
				fetch={(api) =>
					api
						.get("/mail-message-parsings", {
							params: requestParams,
						})
						.then((resp) => resp.data)
				}
				onPageChange={(page) => setParams({ ...params, page })}
			>
				{({ items }) => {
					return (
						<Table
							headers={[
								"From",
								"Subject",
								"Mailbox",
								"Status",
								"Classification",
								"Credits",
								"Processed",
							]}
							responsive
							bordered
							hover
							rows={items.map((row) => {
								const badge = getParsingBadge(row)
								const m = row.mail_message
								const orphaned = Boolean(m?.is_orphaned)
								const subjectText = m?.subject || "(No Subject)"
								return [
									<Inline gap="2" alignItems="center" flexWrap="wrap">
										<Text fontSize="sm">{m?.from_name || "Unknown"}</Text>
										{orphaned ? (
											<Badge
												status="warning"
												fullRounded
												title="Source mail no longer exists — mailbox or message was deleted"
											>
												Orphaned
											</Badge>
										) : null}
									</Inline>,
									<Box minWidth="0">
										{m?.id && !orphaned && !adminMode ? (
											<Link
												// Prefer the RFC Message-ID (an exact match lands on this
												// one message); fall back to a subject search.
												to={`${generatePath("/mail-messages")}?q=${encodeURIComponent(
													m.provider_message_id || m.subject || ""
												)}`}
												fontWeight="semibold"
											>
												{subjectText}
											</Link>
										) : (
											<Text>{subjectText}</Text>
										)}
									</Box>,
									<Text fontSize="sm" color="muted">
										{m?.mailbox?.is_deleted
											? "Deleted mailbox"
											: m?.mailbox?.email || "—"}
									</Text>,
									badge ? (
										<Badge success={badge.color === "success"}>
											{badge.label}
										</Badge>
									) : (
										<Text fontSize="sm" color="muted">
											—
										</Text>
									),
									<Text fontSize="sm">
										{row.classification
											? CLASSIFICATION_LABELS[row.classification] ||
												row.classification
											: "—"}
									</Text>,
									typeof row.credits === "number" ? (
										<Text
											fontSize="sm"
											title={`Wallet credits debited for this parse: ${row.credits}`}
										>
											{Number(row.credits).toFixed(3)}
										</Text>
									) : (
										<Text fontSize="sm" color="muted">
											—
										</Text>
									),
									row.completed_at || row.created_at ? (
										<Text fontSize="sm" color="muted">
											<RelativeTime
												timestamp={row.completed_at || row.created_at}
											/>
										</Text>
									) : (
										<Text fontSize="sm" color="muted">
											-
										</Text>
									),
								]
							})}
						/>
					)
				}}
			</ListView>
		</Search>
	)
}

function Filters() {
	const { user } = useAuthUser()
	const adminMode = !user?.tenant_id
	return (
		<Stack gap="4">
			<SelectInputField name="status" label="Status">
				<option value="">All</option>
				<option value="completed">AI Processed</option>
				<option value="skipped">Smart Skips</option>
			</SelectInputField>
			{adminMode ? (
				<SelectField
					select={SelectMailParsingTenants}
					name="tenant"
					label="Tenant"
					multiple={false}
					placeholder="Tenant"
				/>
			) : null}
		</Stack>
	)
}

function filtersToQuery(filters: TUsageViewFilters): TFiltersInQuery {
	const intervalType = filters.intervalType || "month"
	const query: TFiltersInQuery = {
		intervalType,
		after: dateToQuery(filters.after || startOf(new Date(), intervalType)),
		before: dateToQuery(filters.before || endOf(new Date(), intervalType)),
	}
	if (filters.q) query.q = filters.q
	if (filters.page) query.page = filters.page
	if (filters.status) query.status = filters.status
	if (filters.tenant) {
		query.tenant = `${filters.tenant.id}#${filters.tenant.name}`
	}
	return query
}

function queryToFilters(query: TFiltersInQuery): TUsageViewFilters {
	const intervalType = query.intervalType || "month"
	const filters: TUsageViewFilters = {
		intervalType,
		after: query.after
			? startOf(parseDateFromQuery(query.after), "day")
			: startOf(new Date(), intervalType),
		before: query.before
			? endOf(parseDateFromQuery(query.before), "day")
			: endOf(new Date(), intervalType),
	}
	if (query.q) filters.q = query.q
	if (query.page) filters.page = query.page
	if (query.status) filters.status = query.status
	if (query.tenant) {
		const [idStr, ...rest] = query.tenant.split("#")
		const id = parseInt(idStr, 10)
		if (Number.isFinite(id)) {
			filters.tenant = { id, name: rest.join("#") }
		}
	}
	return filters
}
